import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";

import { BoxEditorLayout } from "../components/BoxEditorLayout";
import { BoxShopifyChannelCard } from "../components/BoxShopifyChannelCard";
import { RuleBoxHistoryCard } from "../components/RuleBoxHistoryCard";
import { RuleBoxRulesCard } from "../components/RuleBoxRulesCard";
import {
  type BoxDuplicateResult,
  type BoxMetaPatch,
  type BoxMetaResult,
  type BoxRefreshInput,
  boxArtUpload,
  boxWrite,
  type PublishVersionInput,
  type PublishVersionResult,
  refreshResultMessage,
} from "../lib/boxEditor";
import { environmentOpenerDefaults, sharedOpenerDefault } from "../lib/openerDefaults";
import { RULE_BOX_HISTORY_TAB } from "../lib/ruleBoxHistory";
import { useTRPC } from "../lib/trpc";
import { useShopifyShop } from "../lib/useShopifyShop";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * One tenant's own box. The layout is shared with the box template editor; this page
 * is the `boxes.*` half of it — env-scoped reads and writes.
 */
export function BoxEditorPage() {
  const { boxId, tenantId } = useParams({ strict: false }) as { boxId: string; tenantId: string };
  const navigate = useNavigate();
  const trpc = useTRPC();
  // Viewers get the full editor read-only; the server forbids their writes anyway.
  const { can } = useTenantRole();
  const queryClient = useQueryClient();
  const box = useQuery(trpc.boxes.get.queryOptions({ boxId }));

  // `boxChannels` has a row for every rule box and every box on the channel, and none where no
  // store is linked. The Shopify tab shows only for a box with a row while the store is linked:
  // a rule box, which Shopify can sell, or one still on the channel, which keeps its switch so it
  // can come off. A rule box's table is generated from its rules, so the editor shows it
  // read-only.
  const { linked: shopLinked } = useShopifyShop();
  const channels = useQuery(trpc.shopify.boxChannels.queryOptions());
  const channel = channels.data?.find((c) => c.boxId === boxId);
  const ruleBox = channel?.ruleBox ?? false;

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.boxes.get.queryOptions({ boxId }).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.pathKey() });
  };

  const openerStyles = useQuery(trpc.openerStyles.list.queryOptions());
  const updateMutation = useMutation(
    trpc.boxes.update.mutationOptions({
      onSuccess: (saved) => {
        invalidate();
        if (!saved.listingSynced) {
          toast.warning(
            "Saved, but the Shopify product didn't update. Save a change again to retry.",
          );
        }
      },
    }),
  );
  const publishMutation = useMutation(
    trpc.boxes.publishVersion.mutationOptions({ onSuccess: invalidate }),
  );
  const refreshMutation = useMutation(
    trpc.boxes.refreshBox.mutationOptions({
      onSuccess: (result) => {
        toast.success(refreshResultMessage(result));
        invalidate();
      },
    }),
  );
  const duplicateMutation = useMutation(
    trpc.boxes.duplicate.mutationOptions({
      onSuccess: (copy) => {
        invalidate();
        toast.success(`Copied as "${copy.name}"`);
        void navigate({
          to: "/tenants/$tenantId/boxes/$boxId",
          params: { tenantId, boxId: copy.id },
        });
      },
    }),
  );
  const startArtUpload = useMutation(trpc.boxes.startArtUpload.mutationOptions());
  const finishArtUpload = useMutation(trpc.boxes.finishArtUpload.mutationOptions());
  const deleteMutation = useMutation(
    trpc.boxes.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.pathKey() });
        void navigate({ to: "/tenants/$tenantId/boxes", params: { tenantId } });
      },
    }),
  );

  return (
    <BoxEditorLayout
      box={box.data}
      isLoading={box.isLoading}
      loadError={box.error?.message ?? null}
      writes={{
        update: boxWrite<BoxMetaPatch, BoxMetaResult>(boxId, updateMutation),
        publish: boxWrite<PublishVersionInput, PublishVersionResult>(boxId, publishMutation),
        refresh: boxWrite<BoxRefreshInput>(boxId, refreshMutation),
        duplicate: boxWrite<BoxRefreshInput, BoxDuplicateResult>(boxId, duplicateMutation),
        remove: boxWrite<BoxRefreshInput>(boxId, deleteMutation),
        uploadArt: boxArtUpload(boxId, startArtUpload, finishArtUpload),
      }}
      // boxes.get is env-scoped and the boxes_env_matches_tenant CHECK means an
      // env-scoped row always has a tenant, so this route never holds a box
      // template. Templates are edited on /templates/$boxId.
      isCatalog={false}
      readOnly={!can("editor")}
      openerStyles={
        openerStyles.data && {
          styles: openerStyles.data.styles,
          inheritLabel: "Same as the environment",
          // Named only when the storefront and Shopify overlay open with the same style.
          inheritedStyleId: sharedOpenerDefault(environmentOpenerDefaults(openerStyles.data)),
        }
      }
      generatedTable={ruleBox ? <RuleBoxTableNotice /> : undefined}
      tabs={[
        ...(shopLinked && channel
          ? [
              {
                value: "shopify",
                label: "Shopify",
                content: (
                  <div className="flex flex-col gap-6">
                    <BoxShopifyChannelCard boxId={boxId} readOnly={!can("editor")} />
                    {ruleBox && <RuleBoxRulesCard boxId={boxId} readOnly={!can("editor")} />}
                  </div>
                ),
              },
            ]
          : []),
        // A rule box's table is refreshed by the server, so its history says what moved and why.
        ...(ruleBox
          ? [
              {
                value: RULE_BOX_HISTORY_TAB,
                label: "History",
                content: <RuleBoxHistoryCard boxId={boxId} />,
              },
            ]
          : []),
      ]}
      // No parentCrumb: inside TenantLayout, whose header owns the trail.
    />
  );
}

/** Above a rule box's read-only drop table: why it can't be edited, and where to go instead. */
function RuleBoxTableNotice() {
  const linkClass = "font-medium text-foreground underline underline-offset-4";
  return (
    <div className="flex items-start gap-3 rounded-md border bg-muted/50 px-4 py-3 text-sm">
      <RefreshCw className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="flex flex-col gap-1">
        <p className="font-medium">This drop table is managed automatically</p>
        <p className="text-muted-foreground">
          It is rebuilt from the box's rules every day, and whenever a product's stock or price
          changes, so it can't be edited here. Change the rules on the{" "}
          <Link
            to="."
            search={(prev: Record<string, unknown>) => ({ ...prev, tab: "shopify" })}
            replace
            className={linkClass}
          >
            Shopify tab
          </Link>{" "}
          or in the Shopify app to change what's inside.{" "}
          <Link
            to="."
            search={(prev: Record<string, unknown>) => ({ ...prev, tab: RULE_BOX_HISTORY_TAB })}
            replace
            className={linkClass}
          >
            See what changed
          </Link>
        </p>
      </div>
    </div>
  );
}
